const MAPS_CALLBACK = '__speedphotoMapsReady';
const ANCHOR_ID = 'booth-finder';
const NEAREST_ZOOM = 12;
const POSITION_TIMEOUT_MS = 15000;
const PIN_WIDTH = 26;
const PIN_HEIGHT = 37;
const PIN_BODY = '#e84b4d';
const PIN_DOT = '#d13f41';
const MAP_BACKGROUND = '#212121';
const MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#212121' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#5c5c5c' }] },
  { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'administrative.locality', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#d6d6d6' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#262626' }] },
  { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2e2e2e' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#383838' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#454545' }] },
  { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#8c3a3b' }] },
  { featureType: 'transit.line', elementType: 'labels.text.fill', stylers: [{ color: '#c26a6b' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3d3d3d' }] },
];

let mapsLoading = null;

function loadMaps({ key, language, region }) {
  if (window.google?.maps?.importLibrary) return Promise.resolve();
  if (mapsLoading) return mapsLoading;

  mapsLoading = new Promise((resolve, reject) => {
    window[MAPS_CALLBACK] = () => resolve();

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?${new URLSearchParams({
      key,
      v: 'weekly',
      loading: 'async',
      callback: MAPS_CALLBACK,
      language,
      region,
    })}`;
    script.async = true;
    script.onerror = () => {
      mapsLoading = null;
      script.remove();
      reject(new Error('maps_failed'));
    };
    document.head.append(script);
  });

  return mapsLoading;
}

function currentPosition() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: false, timeout: POSITION_TIMEOUT_MS, maximumAge: 300000 },
    );
  });
}

function distanceKm(a, b) {
  const rad = (degrees) => (degrees * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;

  return 12742 * Math.asin(Math.sqrt(h));
}

function readStrings(root) {
  try {
    return JSON.parse(root.querySelector('[data-booth-finder-strings]')?.textContent ?? '{}');
  } catch {
    return {};
  }
}

function setup(root) {
  const el = {
    button: root.querySelector('[data-booth-finder-locate]'),
    status: root.querySelector('[data-booth-finder-status]'),
    booth: root.querySelector('[data-booth-finder-booth]'),
    label: root.querySelector('[data-booth-finder-label]'),
    name: root.querySelector('[data-booth-finder-name]'),
    address: root.querySelector('[data-booth-finder-address]'),
    distance: root.querySelector('[data-booth-finder-distance]'),
    media: root.querySelector('[data-booth-finder-media]'),
    map: root.querySelector('[data-booth-finder-map]'),
  };

  if (!el.button || root.dataset.boothFinderReady) return;
  root.dataset.boothFinderReady = 'true';

  const strings = readStrings(root);
  const country = (root.dataset.country ?? '').toUpperCase();
  const language = root.dataset.language || document.documentElement.lang || 'en';
  const numberFormat = new Intl.NumberFormat(language, { maximumFractionDigits: 1 });

  let booths = null;
  let map = null;
  const markers = new Map();
  let active = null;
  let origin = null;
  let youMarker = null;

  function showStatus(message) {
    el.status.textContent = message ?? '';
    el.status.dataset.visible = String(Boolean(message));
  }

  function showBooth(booth, nearest) {
    el.label.textContent = nearest ? strings.nearest : strings.selected;
    el.name.textContent = booth.name;
    el.address.textContent = booth.address ?? booth.city;
    el.distance.textContent = origin
      ? (strings.distance ?? '__KM__ km').replace('__KM__', numberFormat.format(distanceKm(origin, booth)))
      : '';
    el.booth.dataset.visible = 'true';
  }

  async function loadBooths() {
    if (booths) return booths;

    const response = await fetch(root.dataset.boothsUrl);
    const all = response.ok ? await response.json() : [];
    booths = Array.isArray(all) ? all : [];

    return booths;
  }

  function themeColour(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }

  function pinIcon() {
    const body = themeColour('--color-brand') || PIN_BODY;
    const dot = themeColour('--color-brand-hover') || PIN_DOT;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${PIN_WIDTH}" height="${PIN_HEIGHT}" viewBox="0 0 36 51"><path d="M18 0C8 0 0 7.6 0 17.1c0 7.2 4.4 12.4 8.2 18.3C11.3 40.2 14.6 45.3 18 51c3.4-5.7 6.7-10.8 9.8-15.6 3.8-5.9 8.2-11.1 8.2-18.3C36 7.6 28 0 18 0Z" fill="${body}"/><circle cx="18" cy="17.6" r="7" fill="${dot}"/></svg>`;

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(PIN_WIDTH, PIN_HEIGHT),
      anchor: new google.maps.Point(PIN_WIDTH / 2, PIN_HEIGHT),
    };
  }

  function activate(booth, nearest) {
    const previous = active;
    active = booth;

    for (const target of [previous, booth]) {
      const marker = target && markers.get(target);
      if (marker) marker.setZIndex(target === booth ? 1000 : null);
    }

    showBooth(booth, nearest);
  }

  async function renderMap(GoogleMap, Marker) {
    if (!map) {
      map = new GoogleMap(el.map, {
        center: { lat: booths[0].lat, lng: booths[0].lng },
        zoom: 5,
        styles: MAP_STYLE,
        backgroundColor: MAP_BACKGROUND,
        disableDefaultUI: true,
        zoomControl: true,
        fullscreenControl: true,
        clickableIcons: false,
        gestureHandling: 'cooperative',
      });

      const icon = pinIcon();

      for (const booth of booths) {
        const marker = new Marker({
          map,
          position: { lat: booth.lat, lng: booth.lng },
          title: booth.name,
          icon,
        });
        marker.addListener('click', () => activate(booth, false));
        markers.set(booth, marker);
      }
    }

    el.map.dataset.visible = 'true';
    el.media.dataset.map = 'on';

    for (const [booth, marker] of markers) {
      marker.setZIndex(booth === active ? 1000 : null);
    }

    if (origin) {
      if (youMarker) youMarker.setPosition(origin);
      else {
        youMarker = new Marker({
          map,
          position: origin,
          title: strings.you,
          zIndex: 999,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 7,
            fillColor: themeColour('--color-white'),
            fillOpacity: 1,
            strokeColor: themeColour('--color-ink'),
            strokeWeight: 3,
          },
        });
      }
    }

    if (active && origin) {
      map.setCenter({ lat: active.lat, lng: active.lng });
      map.setZoom(NEAREST_ZOOM);
    } else {
      const bounds = new google.maps.LatLngBounds();
      for (const booth of booths) bounds.extend({ lat: booth.lat, lng: booth.lng });
      map.fitBounds(bounds, 24);
    }
  }

  async function locate() {
    el.button.disabled = true;
    showStatus(strings.locating);

    const positionPromise = currentPosition();
    const mapsPromise = loadMaps({ key: root.dataset.mapsKey, language, region: country })
      .then(() =>
        Promise.all([google.maps.importLibrary('maps'), google.maps.importLibrary('marker')]),
      )
      .then(([maps, marker]) => ({ GoogleMap: maps.Map, Marker: marker.Marker }))
      .catch(() => null);

    try {
      const [list, position] = await Promise.all([loadBooths(), positionPromise]);

      if (!list.length) {
        showStatus(strings.noBooths);
        return;
      }

      origin = position;

      if (origin) {
        const nearest = list.reduce((best, booth) =>
          distanceKm(origin, booth) < distanceKm(origin, best) ? booth : best,
        );
        activate(nearest, true);
        showStatus(null);
      } else {
        showStatus(strings.denied);
      }

      const libraries = await mapsPromise;

      if (!libraries) {
        showStatus(strings.mapFailed);
        return;
      }

      await renderMap(libraries.GoogleMap, libraries.Marker);
    } catch {
      showStatus(strings.mapFailed);
    } finally {
      el.button.disabled = false;
    }
  }

  window.gm_authFailure = () => {
    el.map.dataset.visible = 'false';
    delete el.media.dataset.map;
    showStatus(strings.mapFailed);
  };
  el.button.addEventListener('click', locate);

  if (location.hash === `#${ANCHOR_ID}` && root.closest(`#${ANCHOR_ID}`)) locate();
}

function setupAll(scope = document) {
  scope.querySelectorAll('[data-booth-finder]').forEach(setup);
}

function scrollToAnchor() {
  if (location.hash !== `#${ANCHOR_ID}`) return;

  const target = document.getElementById(ANCHOR_ID);
  if (target) target.scrollIntoView({ block: 'start' });
}

setupAll();
document.addEventListener('shopify:section:load', (event) => setupAll(event.target));
scrollToAnchor();
window.addEventListener('load', scrollToAnchor, { once: true });
